import React, { useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import numeral from 'numeral';
import { Link } from 'react-router-dom';
import { Button, notification, Table, Typography, Space, Tag } from 'antd';
import { ColumnType } from 'antd/lib/table';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { FIELDS_NAMES } from './const';
import { CURRENCY_FORMAT } from '../../constants/numberformats';
import routes from '../../constants/routes';
import { UserContext } from '../../contexts/UserContext';
import VoidFeeLedgerModal from '../Modals/VoidFeeLedgerModal';
import RefundFeeLedgerModal from '../Modals/RefundFeeLedgerModal';

dayjs.extend(localizedFormat);

interface IFeeLedgerTableProps {
  data: any;
  isAssignmentIdShown?: boolean;
  showActionButtons?: boolean;
}
const FeeLedgerTable = ({
  data,
  isAssignmentIdShown,
  showActionButtons = false,
}: IFeeLedgerTableProps) => {
  const { dbUser } = useContext(UserContext);
  const [, contextHolder] = notification.useNotification();
  const [isRefundModalVisible, setIsRefundModalVisible] = useState(false);
  const [isVoidModalVisible, setIsVoidModalVisible] = useState(false);

  const assignmentId = data?.[0]?.assignment_id;

  const hasRefundableFees = useMemo(() => {
    if (!data?.length) return false;

    const currentMembership =
      data[0]?.assignment?.user?.user_membership?.[
        data[0]?.assignment?.user?.user_membership.length - 1
      ];
    const currentProcessor = currentMembership?.paymentProcessor?.name;
    return data.some((record: any) => {
      if (
        !record.processed_on ||
        record.refunded_on ||
        record.is_voided ||
        record.amount < 0
      )
        return false;

      const processedDate = dayjs(record.processed_on);
      const activeMembership = record.assignment?.user?.user_membership?.find(
        (membership: any) => {
          const fromDate = dayjs(membership.from);
          const toDate = membership.to ? dayjs(membership.to) : null;
          return (
            processedDate.isAfter(fromDate) &&
            (!toDate || processedDate.isBefore(toDate))
          );
        },
      );

      return activeMembership?.paymentProcessor?.name === currentProcessor;
    });
  }, [data]);

  const hasVoidableFees = useMemo(() => {
    if (!data?.length) return false;

    return data.some((record: any) => {
      return !record.processed_on && !record.is_voided && record.amount > 0;
    });
  }, [data]);

  const columns: ColumnType<any>[] = useMemo(() => {
    return [
      ...(isAssignmentIdShown
        ? [
            {
              title: FIELDS_NAMES.ASSIGNMENT_ID,
              dataIndex: 'assignment_id',
              sorter: (
                recordA: { assignment_id: number },
                recordB: { assignment_id: number },
              ) => recordA.assignment_id - recordB.assignment_id,
              render: (id: number) =>
                dbUser?.userPermissions.ManageAssignments ||
                dbUser?.userPermissions.ViewAssignments ? (
                  <Link to={routes.ASSIGNMENTS + '/' + id}>{id}</Link>
                ) : (
                  id
                ),
              key: 'assignment_id',
            },
          ]
        : []),

      {
        title: FIELDS_NAMES.CONTAINER,
        dataIndex: ['assignment', 'container', 'unique_name'],
        sorter: (recordA: any, recordB: any) =>
          recordA.assignment.container.unique_name.localeCompare(
            recordB.assignment.container.unique_name,
          ),
        render: (name: string, record: any) =>
          dbUser?.userPermissions.ManageCups ||
          dbUser?.userPermissions.ViewCups ? (
            <Link
              to={
                routes.CONTAINERS +
                '/' +
                record.assignment?.container?.container_id
              }
            >
              {name}
            </Link>
          ) : (
            name
          ),
        key: 'assignment.container',
      },
      {
        title: FIELDS_NAMES.AMOUNT,
        dataIndex: 'amount',
        sorter: (recordA: { amount: number }, recordB: { amount: number }) =>
          recordA.amount - recordB.amount,
        render: (amount: number) => numeral(amount).format(CURRENCY_FORMAT),
        key: 'amount',
      },
      {
        title: FIELDS_NAMES.ASSESSED_ON,
        sorter: (
          recordA: { assessed_on: string },
          recordB: { assessed_on: string },
        ) =>
          Number(new Date(recordA.assessed_on)) -
          Number(new Date(recordB.assessed_on)),
        dataIndex: 'assessed_on',
        key: 'assessed_on',
        render: (data: string) =>
          data ? dayjs(data).format('LLL') : '- not set -',
      },
      {
        title: FIELDS_NAMES.PROCESSED_ON,
        sorter: (
          recordA: { processed_on: string },
          recordB: { processed_on: string },
        ) =>
          Number(new Date(recordA.processed_on)) -
          Number(new Date(recordB.processed_on)),
        dataIndex: 'processed_on',
        key: 'processed_on',
        render: (data: string) =>
          data ? dayjs(data).format('LLL') : '- not set -',
      },
      {
        title: 'Payment Processor',
        dataIndex: 'payment_processor',
        key: 'payment_processor',
        width: 150,
        render: (_, record: any) => {
          const currentMembership =
            record.assignment?.user?.user_membership?.[
              record.assignment?.user?.user_membership.length - 1
            ];
          const currentProcessor = currentMembership?.paymentProcessor?.name;
          const currentMembershipStart = currentMembership?.from
            ? dayjs(currentMembership.from)
            : null;

          if (!record.processed_on) {
            return currentProcessor || '- not set -';
          }

          const processedDate = dayjs(record.processed_on);
          if (
            currentMembershipStart &&
            processedDate.isAfter(currentMembershipStart)
          ) {
            return currentProcessor || '- not set -';
          }
          const activeMembership =
            record.assignment?.user?.user_membership?.find(
              (membership: any) => {
                const fromDate = dayjs(membership.from);
                const toDate = membership.to ? dayjs(membership.to) : null;
                return (
                  processedDate.isAfter(fromDate) &&
                  (!toDate || processedDate.isBefore(toDate))
                );
              },
            );

          const historicalProcessor =
            activeMembership?.paymentProcessor?.name || '- not set -';
          const isDifferentFromCurrent =
            historicalProcessor !== currentProcessor;

          return (
            <Typography.Text
              type={isDifferentFromCurrent ? 'secondary' : undefined}
            >
              {historicalProcessor}
            </Typography.Text>
          );
        },
      },
      {
        title: FIELDS_NAMES.NOTE,
        dataIndex: 'note',
        render: (note: string) => note || '- not set -',
        key: 'note',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 180,
        render: (_, record: any) => {
          let tag = null;
          if (record.refunded_on) {
            tag = <Tag color="blue">Refunded</Tag>;
          } else if (record.is_voided) {
            tag = <Tag color="red">Voided</Tag>;
          } else if (!record.processed_on) {
            if (record.amount < 0) {
              tag = <Tag color="default">Not Set</Tag>;
            } else {
              tag = <Tag color="orange">Pending</Tag>;
            }
          } else if (record.note?.includes('refund')) {
            tag = <Tag color="default">Not Set</Tag>;
          } else {
            tag = <Tag color="green">Charged</Tag>;
          }

          const STRIPE_INVOICE_URL = `https://dashboard.stripe.com/${
            process.env.REACT_APP_ENV === 'production' ? '' : 'test/'
          }invoices/`;

          return (
            <div>
              {tag}
              {record.invoice_id && (
                <div style={{ marginTop: 4 }}>
                  <a
                    href={STRIPE_INVOICE_URL + record.invoice_id}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontSize: 12 }}
                  >
                    View Stripe Invoice
                  </a>
                </div>
              )}
            </div>
          );
        },
      },
    ];
  }, [isAssignmentIdShown]);

  return (
    <>
      {contextHolder}
      {showActionButtons && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginBottom: 16,
          }}
        >
          <Space>
            <Button
              type="primary"
              onClick={() => setIsRefundModalVisible(true)}
              disabled={!hasRefundableFees}
            >
              Refund Charges
            </Button>
            <Button
              type="primary"
              onClick={() => setIsVoidModalVisible(true)}
              disabled={!hasVoidableFees}
            >
              Void Fees
            </Button>
          </Space>
        </div>
      )}

      {showActionButtons && (
        <>
          <RefundFeeLedgerModal
            isVisible={isRefundModalVisible}
            onClose={() => setIsRefundModalVisible(false)}
            assignmentId={assignmentId}
            onSuccess={() => {
              setIsRefundModalVisible(false);
            }}
          />

          <VoidFeeLedgerModal
            isVisible={isVoidModalVisible}
            onClose={() => setIsVoidModalVisible(false)}
            assignmentId={assignmentId}
            onSuccess={() => {
              setIsVoidModalVisible(false);
            }}
          />
        </>
      )}
      <Table
        tableLayout="fixed"
        rowKey={'record_id'}
        dataSource={data}
        columns={columns}
        pagination={false}
      />
    </>
  );
};

export default FeeLedgerTable;
